import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import {
  Container,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import { TriviaUserDataContext } from '../../context/TriviaUserDataContext';

let socketInstance = null;

const theme = {
  primary: '#B43361',
  secondary: '#F3D4D4',
  background: '#FFFFFF',
  text: '#000000',
};

const TriviaPlayer = () => {
  const { pin } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(TriviaUserDataContext);
  const [session, setSession] = useState({
    currentQuestionIndex: 0,
    gameEnded: false,
    winners: [],
    question: null,
    players: [], // Ensure players is initialized as an empty array
  });
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const playerName = sessionStorage.getItem('triviaPlayerName');
    const playerEmail = sessionStorage.getItem('triviaPlayerEmail');
    if (!playerName || !playerEmail) {
      navigate('/trivia/join');
      return;
    }

    if (!socketInstance) {
      socketInstance = io('https://bosscrowns-api-a228488a1e46.herokuapp.com', {
        autoConnect: false,
        query: { role: 'player' },
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
      });
    }

    socketInstance.connect();
    console.log(`Player connected to socket for pin ${pin}, player: ${playerEmail}`);

    socketInstance.emit('join-session', pin);

    const handleSessionState = ({ currentQuestionIndex, players, gameEnded, winners, question }) => {
      console.log(`Received session-state for ${pin}:`, { currentQuestionIndex, players, gameEnded, winners, question });
      setSession({
        currentQuestionIndex,
        players: players || [], // Fallback to empty array if players is undefined
        gameEnded,
        winners: winners || [],
        question,
      });
      setSelectedAnswer('');
    };

    const handlePlayerJoined = (players) => {
      console.log(`Players joined in ${pin}:`, players);
      setSession((prev) => ({ ...prev, players: players || [] }));
    };

    const handlePlayerUpdated = (players) => {
      console.log(`Players updated in ${pin}:`, players);
      setSession((prev) => ({ ...prev, players: players || [] }));
    };

    const handleError = ({ message }) => {
      console.error(`Socket error in ${pin}: ${message}`);
      setError(message || 'An error occurred');
      setIsLoading(false);
      navigate('/trivia/join');
    };

    socketInstance.on('session-state', handleSessionState);
    socketInstance.on('player-joined', handlePlayerJoined);
    socketInstance.on('player-updated', handlePlayerUpdated);
    socketInstance.on('game-ended', handleSessionState);
    socketInstance.on('error', handleError);

    const fetchSession = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`https://bosscrowns-api-a228488a1e46.herokuapp.com/bosscrowns/trivia-sessions/${pin}`);
        if (!response.ok) throw new Error('Trivia session not found');
        const data = await response.json();
        const gameTemplate = await fetch(
          `https://bosscrowns-api-a228488a1e46.herokuapp.com/bosscrowns/trivia/${data.gameTemplateId}`
        ).then((res) => res.json());
        setSession({
          ...data,
          players: data.players || [], // Ensure players is an array
          question: gameTemplate.data.questions[data.currentQuestionIndex],
        });
      } catch (err) {
        console.error(`Error fetching trivia session ${pin}:`, err);
        setError('Failed to load session');
        navigate('/trivia/join');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSession();

    return () => {
      console.log(`Cleaning up Player socket for ${pin}`);
      if (socketInstance) {
        socketInstance.off('session-state', handleSessionState);
        socketInstance.off('player-joined', handlePlayerJoined);
        socketInstance.off('player-updated', handlePlayerUpdated);
        socketInstance.off('game-ended', handleSessionState);
        socketInstance.off('error', handleError);
        socketInstance.disconnect();
      }
    };
  }, [pin, navigate]);

  const submitAnswer = useCallback(() => {
    if (!selectedAnswer || session.gameEnded) return;
    setIsLoading(true);
    socketInstance.emit('submit-answer', {
      pin,
      playerName: sessionStorage.getItem('triviaPlayerName'),
      playerEmail: sessionStorage.getItem('triviaPlayerEmail'),
      answer: selectedAnswer,
      questionIndex: session.currentQuestionIndex,
    });
    setSelectedAnswer('');
    setIsLoading(false);
  }, [pin, selectedAnswer, session.gameEnded, session.currentQuestionIndex]);

  const joinAnotherSession = useCallback(async () => {
    try {
      setIsLoading(true);
      navigate('/trivia/join');
    } catch (err) {
      console.error(`Error joining another trivia session from ${pin}:`, err);
      setError('Failed to join another session');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, pin]);

  const isCurrentPlayerWinner =
    session.winners.length > 0 && session.winners[0].email === sessionStorage.getItem('triviaPlayerEmail');

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 }, bgcolor: theme.background }}>
      <Box sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, bgcolor: theme.background }}>
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <CasinoIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: theme.primary, mb: 2 }} />
          <Typography variant="h4" sx={{ color: theme.text, fontWeight: 'bold', mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.8rem', sm: '2.5rem' } }}>
            Trivia Player Dashboard
          </Typography>
          {error && (
            <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {error}
            </Typography>
          )}
          {session.gameEnded && (
            <>
              <Typography
                variant="h5"
                sx={{ color: theme.primary, fontWeight: 'bold', mb: 2, fontSize: { xs: '1.5rem', sm: '2rem' } }}
              >
                {isCurrentPlayerWinner
                  ? 'You Won!'
                  : `Game Over! Winner: ${session.winners[0]?.name || 'Unknown'} (Score: ${session.winners[0]?.score || 0})`}
              </Typography>
              <Button
                variant="contained"
                onClick={joinAnotherSession}
                disabled={isLoading}
                sx={{
                  bgcolor: 'warning.main',
                  '&:hover': { bgcolor: 'warning.dark' },
                  padding: { xs: '8px 16px', sm: '10px 20px' },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  mb: 2,
                }}
              >
                Join Another Session
              </Button>
            </>
          )}
          <Typography variant="h6" sx={{ color: theme.text, mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Session PIN: {pin}
          </Typography>
          {isLoading && <CircularProgress sx={{ mb: 2 }} />}
          {session.question && (
            <Box sx={{ width: '100%', maxWidth: 600, mb: 3, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ color: theme.text, mb: 1 }}>
                Question {session.currentQuestionIndex + 1}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.text, mb: 2 }}>
                {session.question.question}
              </Typography>
              <List dense>
                {session.question.answerType === 'multiple-choice' &&
                  session.question.options.map((option, idx) => (
                    <ListItem
                      key={idx}
                      button
                      onClick={() => setSelectedAnswer(option)}
                      sx={{
                        bgcolor: selectedAnswer === option ? theme.secondary : 'transparent',
                        '&:hover': { bgcolor: theme.secondary },
                      }}
                    >
                      <ListItemText
                        primary={`${idx + 1}. ${option}`}
                        primaryTypographyProps={{ color: theme.text }}
                      />
                    </ListItem>
                  ))}
                {session.question.answerType === 'true-false' && (
                  <>
                    <ListItem
                      button
                      onClick={() => setSelectedAnswer('true')}
                      sx={{
                        bgcolor: selectedAnswer === 'true' ? theme.secondary : 'transparent',
                        '&:hover': { bgcolor: theme.secondary },
                      }}
                    >
                      <ListItemText primary="1. True" primaryTypographyProps={{ color: theme.text }} />
                    </ListItem>
                    <ListItem
                      button
                      onClick={() => setSelectedAnswer('false')}
                      sx={{
                        bgcolor: selectedAnswer === 'false' ? theme.secondary : 'transparent',
                        '&:hover': { bgcolor: theme.secondary },
                      }}
                    >
                      <ListItemText primary="2. False" primaryTypographyProps={{ color: theme.text }} />
                    </ListItem>
                  </>
                )}
              </List>
              <Button
                variant="contained"
                onClick={submitAnswer}
                disabled={!selectedAnswer || isLoading || session.gameEnded}
                sx={{
                  bgcolor: theme.primary,
                  '&:hover': { bgcolor: theme.secondary },
                  mt: 2,
                  padding: { xs: '8px 16px', sm: '10px 20px' },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                Submit Answer
              </Button>
            </Box>
          )}
          <Box sx={{ width: '100%', maxWidth: 600, mb: 3 }}>
            <Typography variant="h6" sx={{ color: theme.text, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Leaderboard
            </Typography>
            {session.players && session.players.length > 0 ? (
              <List dense>
                {session.players
                  .sort((a, b) => b.score - a.score)
                  .map((player, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={`${player.name} (Score: ${player.score})`}
                        secondary={player.email}
                        primaryTypographyProps={{ color: theme.text }}
                        secondaryTypographyProps={{ color: theme.text }}
                      />
                    </ListItem>
                  ))}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: theme.text }}>
                No players have joined yet.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default TriviaPlayer;